import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getApiLanguage } from "src/services/commonLanguageHeader";
import {
  createModelProperty,
  deleteModelProperty,
  getModelPropertiesList,
  getPropertiesByMake,
  getUnitModelsByMake,
  updateModelProperty,
  type ModelPropertyListParams,
  type ModelPropertyPayload,
} from "src/services/administrator/modelPropertyService";
import { useGeneralRefetchOverride } from "src/queries/general.queries";
import { QueryNames } from "src/queries/queries.constant";

export const useQueryGetModelPropertiesList = (
  params: ModelPropertyListParams = {},
  options: { enabled?: boolean } & Record<string, unknown> = {},
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.modelPropertyList, params, language],
      queryFn: () => getModelPropertiesList(params, language),
      refetchOnMount: true,
      retry: false,
      ...options,
    }),
    options.enabled !== false,
  );
};

export const useQueryGetUnitModelsByMake = (
  makeId?: string | number,
  options: { enabled?: boolean } & Record<string, unknown> = {},
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...QueryNames.modelPropertyUnitModelLookup, makeId, language],
    queryFn: () => getUnitModelsByMake(makeId!, language),
    enabled: Boolean(makeId) && options.enabled !== false,
    retry: false,
    ...options,
  });
};

export const useQueryGetPropertiesByMake = (
  makeId?: string | number,
  options: { enabled?: boolean } & Record<string, unknown> = {},
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...QueryNames.modelPropertyParameterLookup, makeId, language],
    queryFn: () => getPropertiesByMake(makeId!, language),
    enabled: Boolean(makeId) && options.enabled !== false,
    retry: false,
    ...options,
  });
};

export const useMutationCreateModelProperty = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: ModelPropertyPayload) => createModelProperty(payload, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.modelPropertyList });
    },
  });
};

export const useMutationUpdateModelProperty = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string | number;
      payload: Partial<ModelPropertyPayload>;
    }) => updateModelProperty(id, payload, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.modelPropertyList });
    },
  });
};

export const useMutationDeleteModelProperty = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (id: string | number) => deleteModelProperty(id, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.modelPropertyList });
    },
  });
};
